import React from "react";
import { InfiniteScroll, Button } from "nq-component";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../../../usecases/object";
import BaseListPage from "../../../../base/BaseListPage";
import Table from "../../../../components/Table";
import PayrollTablePresenter from "./PayrollTablePresenter";
import { _getExcludedFields } from "./utils/fieldExclusionMap";
import {
  _appendWithKeys,
  _appendWithFields,
  _getExtrasAndDeductions,
  _expandEmployeeInFieldOrder,
  EMPLOYEE_TABLE_FIELD_KEYS,
  _getEarnedPeriodLabel,
} from "./utils/helperFunction";
import { _getHeaders_Reg } from "./utils/headersRegularPayroll";
import { _getHeader_SP } from "./utils/headersSpecialPayroll";
import {
  mergeHeaders,
  convertLabels,
} from "../../Payroll/helper-payroll/HelperFunction";
import Progress from "../../../../components/Progress";

export default class PayrollTablePage extends BaseListPage {
  constructor(props) {
    super(props);
    this.presenter = new PayrollTablePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase()
    );
    this.state = {
      objects: [],
      count: 0,
      limit: 500,
      current: 1,
      panelState: {},
    };
  }

  async componentDidMount() {
    this.presenter.componentDidMount();
    const { object } = this.props;

    try {
      const [otherDeduction, contributions, incentives] = await Promise.all([
        findObjectUseCase().execute("other_deductions", {
          include: ["accTitleRelation.accountsTitle", "employmentTypes"],
          where: {
            payrollTypes: object?.payrollType,
            "employmentTypes.id": object?.employmentType?.id,
            payItems: object?.payItem,
          },
        }),
        findObjectUseCase().execute("contribution_type"),
        findObjectUseCase().execute("incentive", {
          include: ["allowanceType"],
          where: {
            allowanceType: { AllowanceType: object?.payItem },
          },
          keys: ["otherAdditionals", "otherDeductions"],
        }),
      ]);

      const contributionType = contributions.map(
        (item) => item.contributionType
      );
      const extrasAndDeductions = _getExtrasAndDeductions(otherDeduction);

      const deductionLists = {
        deductionType: extrasAndDeductions.DEDUCTION,
        additionalType: extrasAndDeductions.ADDITIONAL,
        contributionType,
        incentives,
      };

      const [tableHeader, primarySubHdr, secondarySubHdr] =
        object?.payrollType !== "Special Payroll"
          ? _getHeaders_Reg(object, deductionLists)
          : _getHeader_SP(object, deductionLists);

      const params = [tableHeader, primarySubHdr, secondarySubHdr];
      const mergedResult = mergeHeaders(...params);
      const convertedHeaders = convertLabels(mergedResult);

      const tableProps = {
        tableHeader,
        primarySubHdr,
        secondarySubHdr,
        convertedHeaders,
      };

      this.setState((prevState) => ({
        panelState: {
          ...prevState.panelState,
          tableProps,
        },
      }));
    } catch (error) {
      console.log("Something went wrong!", error);
    }
  }

  getCollectionName() {
    return this.props.formData?.isBackPay ? "payroll_back_pay" : "payroll";
  }

  async refetchData() {
    const objects = await this.presenter.getObjects();
    this.filterSubmit({});
    return objects;
  }

  onClickPagination(page) {
    this.presenter.setCurrentPage(page);
  }

  setObjects(objects) {
    this.setState({ objects });
  }

  setCurrent(current) {
    this.setState({ current });
  }

  _getViewProps() {
    return this.props.object;
  }

  isJoItem(){
    return this.props.object?.jobType === "Job Order";
  }

  _withEarnedPeriodLabels(fields, object) {
    const nextFields = { ...fields };

    if (nextFields.earnedForThePeriodFrom) {
      nextFields.earnedForThePeriodFrom = {
        ...nextFields.earnedForThePeriodFrom,
        label: _getEarnedPeriodLabel(object?.startDate),
      };
    }

    if (nextFields.earnedForThePeriodTo) {
      nextFields.earnedForThePeriodTo = {
        ...nextFields.earnedForThePeriodTo,
        label: _getEarnedPeriodLabel(object?.endDate),
      };
    }

    return nextFields;
  }

  render() {
    const schema = this.getSchema(this.getCollectionName());

    const { objects, count, current, limit, loading, panelState } = this.state;
    const { object } = this.props;

    console.log("objects", objects);

    const isJoItem = this.isJoItem();

    const fieldsToExclude = _getExcludedFields(object);
    const tableFieldOrder = _expandEmployeeInFieldOrder(fieldsToExclude);
    const filteredFieldSchema = Object.fromEntries(
      fieldsToExclude
        .filter((field) => schema.fields[field])
        .map((field) => [field, schema.fields[field]])
    );

    const newObjects = _appendWithKeys(objects, object);
    const newFields = this._withEarnedPeriodLabels(
      _appendWithFields(filteredFieldSchema, object, isJoItem),
      object
    );

    const dynamicObject = {
      contributions: object?.contributions,
      deductions: object?.deductions,
      incentives: object?.incentives,
      contributionsLabel: "MANDATORY DEDUCTIONS",
      deductionsLabel: "LOANS AND OTHER DEDUCTIONS",
    };

    if (!schema) return <Progress />;
    return (
      <>
        <div>
          <InfiniteScroll loadMore={this.loadMore.bind(this)}>
            <Table
              progress={loading}
              refetch={this.refetchData.bind(this)}
              fields={newFields}
              objects={newObjects}
              collection={schema}
              count={count}
              limit={limit}
              current={current}
              loadPagination={this.onClickPagination.bind(this)}
              isExcludeFieldOrder={true}
              excludeOrderFields={tableFieldOrder}
              dynamicObject={dynamicObject}
              excludeFields={Object.keys(schema.fields)
                .reduce(
                  (acc, key) => {
                    const options = schema.fields[key];
                    if (options.read === false) {
                      acc.push(key);
                    }
                    switch (options._type || options.type) {
                      case "Relation":
                      // case "Array":
                      case "Object":
                      case "File":
                        acc.push(key);
                        break;
                      default:
                    }
                    if (!fieldsToExclude.includes(key)) {
                      acc.push(key);
                    }
                    return acc;
                  },
                  ["id", "Created At", "Updated At", "employee"]
                )
                .filter((key) => !EMPLOYEE_TABLE_FIELD_KEYS.includes(key))}
              onClickItem={this.onClickItem.bind(this)}
            />
          </InfiniteScroll>
          <br></br>
          <div className="d-flex align-items-center">
            <div style={{ marginLeft: "auto", display: "flex", gap: "10px" }}>
              <Button
                onClick={() =>
                  this.presenter._handleExportData({
                    objects,
                    fieldsToExclude,
                    object,
                  })
                }
              >
                <i className="bi bi-arrow-down-circle"></i> Export
              </Button>
              <Button onClick={() => this.presenter._handlePrintData()}>
                <i className="bi bi-printer"></i> Print
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }
}
